import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import type { FeatureKey } from '@platform/contracts';
import { Alert, Card, Spinner, useToast } from '@platform/ui';

import { FeaturesTab } from '../../../features/tenants/features-tab';
import { usePlans, useRefreshTenant, useTenantDetail } from '../../../features/tenants/use-tenant';
import { FEATURE_LABELS } from '../../../lib/api/platform';
import { usePlatformClient } from '../../../lib/api/platform-client-context';

export const Route = createFileRoute('/tenants/$tenantId/features')({
  component: TenantFeaturesPage,
});

/** `/tenants/$id/features` — plan and per-tenant feature overrides (FR-PLAT-04). */
function TenantFeaturesPage() {
  const { tenantId } = Route.useParams();
  const client = usePlatformClient();
  const refresh = useRefreshTenant(tenantId);
  const { toast } = useToast();
  const tenant = useTenantDetail(tenantId);
  const plans = usePlans();

  /* Changing the plan is `PATCH /platform/tenants/:id` with one field: there is no
     separate "set plan" endpoint, and inventing one in the seam would hide that a plan
     change is an ordinary tenant update (API.md §2.13). */
  const setPlan = useMutation({
    mutationFn: (planId: string) => client.updateTenant(tenantId, { planId }),
    onSuccess: async (updated) => {
      toast({ tone: 'success', message: `Plan changed to ${updated.planName}.` });
      await refresh();
    },
    onError: (cause: Error) => {
      toast({ tone: 'error', message: cause.message });
    },
  });

  const setFeature = useMutation({
    mutationFn: (input: { key: FeatureKey; enabled: boolean | null }) =>
      client.setTenantFeature(tenantId, input.key, input.enabled),
    onSuccess: async (_features, input) => {
      toast({
        tone: 'success',
        message:
          input.enabled === null
            ? `${FEATURE_LABELS[input.key]} follows the plan again.`
            : `${FEATURE_LABELS[input.key]} is overridden ${input.enabled ? 'on' : 'off'}.`,
      });
      await refresh();
    },
    onError: (cause: Error) => {
      toast({ tone: 'error', message: cause.message });
    },
  });

  if (tenant.isPending || plans.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading features" />
      </Card>
    );
  }
  if (tenant.isError || plans.isError) {
    return <Alert tone="error">The plan and features could not be loaded.</Alert>;
  }

  return (
    <FeaturesTab
      tenant={tenant.data.tenant}
      plans={plans.data}
      features={tenant.data.features}
      readOnly={tenant.data.tenant.status === 'archived'}
      onPlanChange={(planId) => {
        setPlan.mutate(planId);
      }}
      onOverrideChange={(key, enabled) => {
        setFeature.mutate({ key, enabled });
      }}
    />
  );
}
